import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { Steps } from 'antd';
import styled from 'styled-components';
import moment from 'moment';
import { Box } from 'atoms';
import { getClientListV2, getClientByIdV2 } from 'store/clientSliceV2';
import { getProjectListV2 } from 'store/projectSliceV2';
import { searchVehicleTypesV2, searchVehiclesV2 } from 'store/vehicleSliceV2';
import { searchBankListV2 } from 'store/vendorSliceV2';
import {
	fetchBillableTripsV2,
	bulkFetchBillableTripsV2,
	createClientInvoiceV2,
	searchClientPOsV2,
	searchClientWOsV2,
	searchSacCodesV2,
	buildClientInvoicePreviewV2,
	getClientInvoiceDetailV2,
	searchRipplrAddressV2,
} from 'store/clientInvoiceSliceV2';
import { useAuth } from 'hooks';
import { getUIVersion } from 'utils/router-static-data';
import { BULK_TRIP_IDS_ENTITY } from 'componentsv2/common/BulkTripFetchModal';
import TripSelectionStep, { SHIPPING_METHOD_OPTIONS } from './components/TripSelectionStep';
import { ALL_VALUE } from './components/MultiSelectDropdown';
import InvoiceGenerationStep from './components/InvoiceGenerationStep';
import InvoiceReviewModal from './components/InvoiceReviewModal';
import InvoiceSuccessModal from './components/InvoiceSuccessModal';

const { Step } = Steps;

const API_DATE_FMT = 'YYYY-MM-DD';
const SHIPPING_METHOD_API = {
	road: 'Road',
	rail: 'Rail',
	water: 'Water',
	warehouse: 'Warehousing',
};
const toApiShippingMethod = (methods = []) => SHIPPING_METHOD_API[methods[0]] || undefined;
const toAllOrList = (arr) => (Array.isArray(arr) && arr.includes(ALL_VALUE) ? 'all' : arr);
// Backend expects the selected Ripplr Bill-From as `from_address_id` (positive int —
// the ripplr_addresses_v2 id). The static fallback issuer has a non-numeric value, so
// coerce and drop it when it isn't a real id.
const toAddressId = (v) => {
	const n = Number(v);
	return Number.isInteger(n) && n > 0 ? n : undefined;
};
const resolveInvoiceDueDate = (responseDue, metaDue) => {
	const d = responseDue || metaDue;
	return d ? moment(d).format(API_DATE_FMT) : '-';
};
const FIXED_ISSUER = {
	value: 'intelligent-retail',
	label: 'Bengaluru, Karnataka',
	legal_name: 'Intelligent Retail Pvt Ltd',
	address:
		'No 413/C, Ground & First Floor, 15th Cross Rd, Muneswara Nagar, ' +
		'6th Sector, HSR Layout, Bengaluru 560102, Karnataka, India',
	gstin: '29AAFCI1726B1ZW',
	pan: 'AAFCI1726B',
	website: 'www.ripplr.in',
	banks: [],
	terms: [
		'Payment has to be cleared as per the payment terms mentioned in the contract.',
		'Please include the Invoice number in your payment notes.',
		'If there is any discrepancy in our invoice, it should be brought to our notice in writing within 7 days from the date of Invoice. Otherwise all charges are deemed correct.',
		'All Cheque/DDs should be drawn in Favour of "Intelligent Retail Private Limited".',
		'Payment to be made in favor of Intelligent Retail Private Limited.',
	],
	signatory: 'ABHISHEK NEHRU',
	contact_email: 'accounts@ripplr.in',
	bank_details: {},
};
const buildRipplrIssuer = (row = {}) => ({
	...row,
	value: row.id ?? row.value,
	terms: Array.isArray(row.terms) && row.terms.length ? row.terms : FIXED_ISSUER.terms,
	website: row.website || FIXED_ISSUER.website,
	signatory: row.signatory || FIXED_ISSUER.signatory,
	contact_email: row.contact_email || FIXED_ISSUER.contact_email,
	bank_details:
		row.bank_details && Object.values(row.bank_details).some(Boolean) ? row.bank_details : {},
});

const formatClientAddress = (addr) => {
	if (!addr || typeof addr !== 'object') return '';
	return [addr.address_line1, addr.address_line2, addr.city, addr.state, addr.pincode]
		.map((p) => (p == null ? '' : String(p).trim()))
		.filter(Boolean)
		.join(', ');
};
const toOrderOptions = (rows, key) =>
	(rows || []).map((r) => ({
		value: r[key],
		label: r.valid_till ? `${r[key]} (valid till ${r.valid_till})` : r[key],
	}));

const clientBillToAddress = (client) => {
	const billing = Array.isArray(client?.billing_addresses) ? client.billing_addresses : [];
	return formatClientAddress(client?.head_office_address || billing[0]);
};

const extractClientGstPan = (client) => {
	const docs = Array.isArray(client?.business_documents) ? client.business_documents : [];
	const gstDoc = docs.find(
		(d) => String(d?.document_type).toUpperCase() === 'GST' && (d?.gst_number || d?.gstin)
	);
	const panDoc = docs.find(
		(d) => String(d?.document_type).toUpperCase() === 'PAN' && d?.pan_number
	);
	const gstin = gstDoc?.gst_number || gstDoc?.gstin || '';
	const pan = panDoc?.pan_number || (gstin.length >= 12 ? gstin.slice(2, 12) : '');
	return { gstin, pan };
};

const PageWrapper = styled(Box)`
	background: #f5f6f8;
	min-height: calc(100vh - 64px);
`;

const StepsCard = styled(Box)`
	background: #fff;
	border-radius: 8px;
	padding: 20px 32px;
	margin: 24px 32px 0;
`;

const StyledSteps = styled(Steps)`
	max-width: 520px;
	margin: 0 auto;

	.ant-steps-item-process .ant-steps-item-icon,
	.ant-steps-item-finish .ant-steps-item-icon {
		background-color: #1e3a5f;
		border-color: #1e3a5f;
	}
	.ant-steps-item-finish .ant-steps-item-icon > .ant-steps-icon {
		color: #fff;
	}
	.ant-steps-item-title {
		font-size: 13px;
	}
`;

const Content = styled(Box)`
	padding: 24px 32px 40px;
`;
const StepPane = styled(Box)`
	display: ${(props) => (props.hidden ? 'none' : 'block')};
`;

const STEPS = [{ title: 'Trips Selection' }, { title: 'Invoice Generation' }];

function CreateClientInvoicePageV2() {
	const navigate = useNavigate();
	const location = useLocation();
	const dispatch = useDispatch();
	const authData = useAuth();
	const invoicePerms =
		getUIVersion() === 'new' ? authData?.v2_permissions : authData?.permissions;
	const canCreateInvoice = !!(
		invoicePerms?.create?.includes('client_invoice') || invoicePerms?.create?.includes('all')
	);

	const [clients, setClients] = useState([]);
	const [projects, setProjects] = useState([]);
	const [vehicleTypes, setVehicleTypes] = useState([]);
	const [vehicleNumbers, setVehicleNumbers] = useState([]);
	const [vehicleSearchLoading, setVehicleSearchLoading] = useState(false);
	const [invoiceContext, setInvoiceContext] = useState(null);
	const [successOpen, setSuccessOpen] = useState(false);
	const [successSummary, setSuccessSummary] = useState({});
	const [banks, setBanks] = useState([]);
	const [bankAccounts, setBankAccounts] = useState([]);
	const [ripplrIssuers, setRipplrIssuers] = useState([]);
	const [previewOpen, setPreviewOpen] = useState(false);
	const [previewPayload, setPreviewPayload] = useState(null);
	const [generating, setGenerating] = useState(false);
	const currentStep = location.state?.wizardStep === 1 && invoiceContext ? 1 : 0;

	const formMeta = {
		issuers: ripplrIssuers.length ? ripplrIssuers : [FIXED_ISSUER],
		sac_codes: [],
		account_types: [],
		banks,
	};

	useEffect(() => {
		dispatch(
			getClientListV2({
				filter_by: { is_active: true },
				pagination: { page: 1, per_page: 1000 },
			})
		)
			.then((res) => {
				const data = res?.rows || res?.data || [];
				setClients(
					data.map((item) => ({
						value: item?.id,
						label:
							item?.client_legal_name ||
							item?.company_name ||
							item?.client_info?.client_name ||
							item?.client_name ||
							item?.short_name ||
							item?.username ||
							'-',
					}))
				);
			})
			.catch(() => setClients([]));

		dispatch(
			getProjectListV2({
				filter_by: { is_active: true },
				pagination: { page: 1, per_page: 1000 },
			})
		)
			.then((res) => {
				setProjects(
					(res?.rows || []).map((item) => ({
						value: item?.id,
						label: item?.project_name || '-',
						client_id: item?.client_id,
						// projects_v2.project_type — lets Step 1 pin the shipping method
						// to Warehouse when a warehousing site is picked.
						project_type: item?.project_type,
					}))
				);
			})
			.catch(() => setProjects([]));

		dispatch(
			searchVehicleTypesV2({
				// `minimal` skips per-row usage/creator lookups on the backend (huge N+1)
				// — the dropdown only needs id + name, so this loads fast.
				filter_by: { is_active: true, minimal: true },
				pagination: { page: 1, per_page: 1000 },
			})
		)
			.then((res) => {
				setVehicleTypes(
					(res?.rows || []).map((item) => ({
						value: item?.id,
						label: item?.vehicle_type_name || item?.name || '-',
					}))
				);
			})
			.catch(() => setVehicleTypes([]));

		dispatch(
			searchVehiclesV2({
				filter_by: { is_active: true },
				pagination: { page: 1, per_page: 2000 },
			})
		)
			.then((res) => {
				setVehicleNumbers(
					(res?.rows || []).map((item) => ({
						value: item?.vehicle_number,
						label: item?.vehicle_number || '-',
						vehicle_type_id: item?.vehicle_type_id,
					}))
				);
			})
			.catch(() => setVehicleNumbers([]));

		dispatch(searchBankListV2({ filter_by: {}, limit: 1000 }))
			.then((res) => {
				const rows = res?.results || [];
				setBankAccounts(rows);
				setBanks(
					rows.map((bank) => ({
						value: bank.bank_name,
						label: bank.bank_name,
					}))
				);
			})
			.catch(() => {
				setBanks([]);
				setBankAccounts([]);
			});
		dispatch(searchRipplrAddressV2({}))
			.then((rows) => setRipplrIssuers((rows || []).map(buildRipplrIssuer)))
			.catch(() => setRipplrIssuers([]));
	}, [dispatch]);

	const handleFetchTrips = async (filters, options = {}) => {
		const res = await dispatch(
			fetchBillableTripsV2({
				filter_by: {
					client_id: filters.client_id,
					project_ids: filters.project_ids,
					vehicle_type_ids: toAllOrList(filters.vehicle_types),
					vehicle_numbers: toAllOrList(filters.vehicle_numbers),
					shipping_method: toApiShippingMethod(filters.shipping_methods),
					start_date: filters.from_date
						? moment(filters.from_date).format(API_DATE_FMT)
						: undefined,
					end_date: filters.to_date
						? moment(filters.to_date).format(API_DATE_FMT)
						: undefined,
					trip_types: filters.trip_types?.length ? filters.trip_types : undefined,
					trip_search: options.tripSearch || undefined,
				},
				showLoading: !options.isSearch,
			})
		);
		return res?.rows || [];
	};
	const handleBulkFetchTrips = async (filters, file) => {
		const res = await dispatch(
			bulkFetchBillableTripsV2({
				filter_by: {
					client_id: filters.client_id,
					project_ids: filters.project_ids,
				},
				entity: BULK_TRIP_IDS_ENTITY,
				file,
			})
		);
		return { rows: res?.rows || [], bulkFetch: res?.bulkFetch || null };
	};
	const handleSearchVehicles = async (term) => {
		setVehicleSearchLoading(true);
		try {
			const res = await dispatch(
				searchVehiclesV2({
					filter_by: { is_active: true, vehicle_number: term },
					pagination: { page: 1, per_page: 50 },
				})
			);
			const found = (res?.rows || []).map((item) => ({
				value: item?.vehicle_number,
				label: item?.vehicle_number || '-',
				vehicle_type_id: item?.vehicle_type_id,
			}));
			setVehicleNumbers((prev) => {
				const seen = new Set(prev.map((v) => v.value));
				return [...found.filter((v) => v.value && !seen.has(v.value)), ...prev];
			});
		} finally {
			setVehicleSearchLoading(false);
		}
	};

	const handleProceed = async ({ filters, selectedTrips, isWarehouseFlow }) => {
		const clientLabel = clients.find((c) => c.value === filters.client_id)?.label || '';
		// Several projects can be billed on one invoice; the header shows them all.
		const selectedProjects = (filters.project_ids || [])
			.map((id) => projects.find((p) => p.value === id))
			.filter(Boolean);
		const projectLabel = selectedProjects.map((p) => p.label).join(', ');
		const shippingLabels = (filters.shipping_methods || []).map(
			(v) => SHIPPING_METHOD_OPTIONS.find((o) => o.value === v)?.label || v
		);
		const pos = await dispatch(
			searchClientPOsV2({ client_id: filters.client_id, project_ids: filters.project_ids })
		);
		const poOptions = toOrderOptions(pos, 'po_number');
		const wos = await dispatch(
			searchClientWOsV2({ client_id: filters.client_id, project_ids: filters.project_ids })
		);
		const woOptions = toOrderOptions(wos, 'wo_number');
		let preview = null;
		try {
			// Called for the warehouse flow too (not just trip-based invoices) — this is
			// what lets `_gather_items` on the backend price the default line item from
			// the Warehousing module's computed MTD revenue instead of a hardcoded 0.
			// `trip_ids` is simply empty for warehouse, which the backend already branches
			// on independently via `shipping_method`.
			preview = await dispatch(
				buildClientInvoicePreviewV2({
					req_data: {
						client_id: filters.client_id,
						project_ids: filters.project_ids,
						shipping_method: toApiShippingMethod(filters.shipping_methods),
						trip_ids: (selectedTrips || []).map((t) => t.trip_id),
						// Default Bill-From (first issuer) so the preview's GST split
						// (IGST vs CGST/SGST) matches the issuer selected by default
						// in the next step.
						from_address_id: toAddressId(formMeta.issuers[0]?.value),
						groupings: 'by_vehicle',
						// The range picked on the trip-selection step. For warehousing it
						// selects which months' slab breakdowns the preview reports; the
						// backend widens it to whole months.
						bill_start_time: filters.from_date
							? moment(filters.from_date).format(API_DATE_FMT)
							: undefined,
						bill_end_time: filters.to_date
							? moment(filters.to_date).format(API_DATE_FMT)
							: undefined,
					},
					setErrors: () => {},
				})
			);
		} catch (e) {
			// Trip-based invoices can't proceed without a preview (it's the only source
			// of billable amounts). Warehouse invoices can still fall back to a manual
			// entry row, so let those continue to Step 2 instead of getting stuck here.
			if (!isWarehouseFlow) {
				return;
			}
		}
		const sacCodes = await dispatch(
			searchSacCodesV2({ shipping_method: toApiShippingMethod(filters.shipping_methods) })
		).catch(() => []);
		const clientRecord = await dispatch(getClientByIdV2({ id: filters.client_id })).catch(
			() => null
		);
		const { gstin: clientGstFromRecord, pan: clientPanFromRecord } =
			extractClientGstPan(clientRecord);
		const clientGstin = clientGstFromRecord || preview?.bill_to_gstin || '';
		const clientPan =
			clientPanFromRecord || (clientGstin.length >= 12 ? clientGstin.slice(2, 12) : '');

		setInvoiceContext({
			filters,
			selectedTrips,
			isWarehouseFlow,
			client: clientLabel,
			project: projectLabel,
			projects: selectedProjects,
			shippingMethods: shippingLabels,
			shippingMethod: toApiShippingMethod(filters.shipping_methods),
			poOptions,
			woOptions,
			preview,
			clientGstin,
			clientPan,
			billTo: clientBillToAddress(clientRecord),
			issuers: formMeta.issuers,
			sacCodes: sacCodes || [],
			accountTypes: formMeta.account_types,
			banks: formMeta.banks,
			bankAccounts,
		});
		navigate(location.pathname, { state: { wizardStep: 1 } });
	};

	const handleGenerate = (invoicePayload) => {
		setPreviewPayload(invoicePayload);
		setPreviewOpen(true);
	};
	const handleConfirmGenerate = async () => {
		const invoicePayload = previewPayload || {};
		const { meta = {}, billTo, shipTo, lineItems = [], clientGstin, taxMode } = invoicePayload;
		const line_items = lineItems
			.filter((r) => !r.is_additional)
			.map((r) => ({
				item_code: r.item,
				description: r.description,
				sac: r.sac_code,
				qty: r.qty,
				rate: r.rate,
				trip_ids: r.trip_ids || [],
			}));
		// Deductions are discounts on the supply, so they travel as negative-amount
		// charge lines and the backend nets them out of their own slab's taxable value.
		// They are deliberately no longer folded into customer_debit_amount, which is a
		// post-tax settlement field (see the payment rollup) and cannot move the GST base.
		const additional_charges = lineItems
			.filter((r) => r.is_additional)
			.map((r) => ({
				description: r.description,
				sac: r.sac_code,
				amount: r.amount,
				is_deduction: Boolean(r.is_deduction),
			}));

		const billFromDate = invoiceContext?.filters?.from_date;
		const billToDate = invoiceContext?.filters?.to_date;
		const req_data = {
			client_id: invoiceContext?.filters?.client_id,
			project_ids: invoiceContext?.filters?.project_ids,
			shipping_method: invoiceContext?.shippingMethod,
			trip_ids: (invoiceContext?.selectedTrips || []).map((t) => t.trip_id),
			invoice_date: moment().format(API_DATE_FMT),
			due_date: meta.due_date ? moment(meta.due_date).format(API_DATE_FMT) : undefined,
			po_number: meta.po_number,
			wo_number: meta.wo_number,
			// Bill period the user searched trips with (Trip Selection step) — persisted
			// on the invoice so it can be shown back verbatim on the detail page.
			bill_start_time: billFromDate ? moment(billFromDate).format(API_DATE_FMT) : undefined,
			bill_end_time: billToDate ? moment(billToDate).format(API_DATE_FMT) : undefined,
			bill_period:
				billFromDate && billToDate
					? `${moment(billFromDate).format('MM/DD/YYYY')} - ${moment(billToDate).format(
							'MM/DD/YYYY'
					  )}`
					: undefined,
			// Selected Ripplr Bill-From → from_address_id (int). The backend resolves the
			// issuer GSTIN + address from it (drives the GST split + PDF Bill-From block).
			from_address_id: toAddressId(meta.bill_from),
			bill_to_gstin: (clientGstin ?? invoiceContext?.clientGstin ?? '').trim() || undefined,
			tax_mode: taxMode,
			bill_to_address: billTo,
			ship_to_address: shipTo,
			line_items,
			additional_charges,
			bank_details: {
				bank_name: meta.bank,
				account_number: meta.account_number,
				ifsc: meta.ifsc,
				account_type: meta.account_type,
			},
			remarks: meta.remarks,
		};

		setGenerating(true);
		const res = await dispatch(createClientInvoiceV2({ req_data, setErrors: () => {} })).catch(
			() => null
		);
		setGenerating(false);
		if (res?.status !== 'success') return;

		setSuccessSummary({
			client: invoiceContext?.client,
			project: invoiceContext?.project,
			invoiceNo: res?.data?.invoice_no,
			invoiceId: res?.data?.id,
			pdfUrl: res?.data?.pdf_url,
			total: res?.data?.grand_total ?? invoicePayload?.totals?.total,
			dueDate: resolveInvoiceDueDate(res?.data?.due_date, meta.due_date),
		});
		setPreviewOpen(false);
		setSuccessOpen(true);
	};
	const handleDownloadSuccess = async () => {
		if (successSummary.pdfUrl) {
			window.open(successSummary.pdfUrl);
			return;
		}
		if (!successSummary.invoiceId) return;
		const detail = await dispatch(
			getClientInvoiceDetailV2({ id: successSummary.invoiceId })
		).catch(() => null);
		const url = detail?.invoice?.pdf_url || detail?.pdf_url;
		if (url) window.open(url);
	};

	if (!canCreateInvoice) {
		return (
			<PageWrapper>
				<Content>
					<Box
						style={{
							textAlign: 'center',
							padding: '80px 0',
							color: '#64748b',
							fontSize: 16,
						}}
					>
						You are not authorized to generate client invoices.
					</Box>
				</Content>
			</PageWrapper>
		);
	}

	return (
		<PageWrapper>
			<StepsCard>
				<StyledSteps current={currentStep} labelPlacement="vertical">
					{STEPS.map((s) => (
						<Step key={s.title} title={s.title} />
					))}
				</StyledSteps>
			</StepsCard>

			<Content>
				<StepPane hidden={currentStep !== 0}>
					<TripSelectionStep
						clients={clients}
						projects={projects}
						vehicleTypes={vehicleTypes}
						vehicleNumbers={vehicleNumbers}
						onFetchTrips={handleFetchTrips}
						onBulkFetchTrips={handleBulkFetchTrips}
						onProceed={handleProceed}
						onSearchVehicleNumbers={handleSearchVehicles}
						vehicleSearchLoading={vehicleSearchLoading}
					/>
				</StepPane>
				{currentStep === 1 && (
					<InvoiceGenerationStep
						context={invoiceContext}
						onBack={() => navigate(-1)}
						onGenerate={handleGenerate}
					/>
				)}
			</Content>

			<InvoiceReviewModal
				visible={previewOpen}
				context={invoiceContext}
				payload={previewPayload}
				loading={generating}
				onBack={() => setPreviewOpen(false)}
				onConfirm={handleConfirmGenerate}
			/>

			<InvoiceSuccessModal
				visible={successOpen}
				summary={successSummary}
				onDownload={handleDownloadSuccess}
				onContinue={() => {
					setSuccessOpen(false);
					navigate(
						successSummary.invoiceId
							? `/client-invoices/${successSummary.invoiceId}`
							: '/client-invoices'
					);
				}}
			/>
		</PageWrapper>
	);
}

export default CreateClientInvoicePageV2;
